import React, { useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useStore } from 'logic';
import { designTokens } from 'theme';

interface DuplicationRequestManagementProps {
  open: boolean;
  onClose: () => void;
}

// Check icon
const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    style={{ width: 18, height: 18 }}
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// Close icon
const CloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    style={{ width: 18, height: 18 }}
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// Person icon
const PersonIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    style={{ width: 18, height: 18 }}
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

export const DuplicationRequestManagement = observer(
  ({ open, onClose }: DuplicationRequestManagementProps) => {
    const { uiDuplicationRequestStore } = useStore();
    const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
    const [responseMessage, setResponseMessage] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    // Requests are automatically loaded via real-time listener in the store
    // No need to manually load here

    const pendingRequests = uiDuplicationRequestStore.pendingRequests;
    const selectedRequest = selectedRequestId
      ? uiDuplicationRequestStore.duplicationRequests[selectedRequestId]
      : null;

    // Clean up selectedRequestId if the request is removed from the store
    useEffect(() => {
      if (selectedRequestId && !selectedRequest) {
        setSelectedRequestId(null);
        setResponseMessage('');
      }
    }, [selectedRequestId, selectedRequest]);

    const handleApprove = async (requestId: string) => {
      setIsProcessing(true);
      try {
        await uiDuplicationRequestStore.approveDuplicationRequest(
          requestId,
          responseMessage.trim() || undefined,
        );
        setResponseMessage('');
        setSelectedRequestId(null);
        // Real-time listener will automatically update the requests
      } catch (error) {
        console.error('Failed to approve request:', error);
      } finally {
        setIsProcessing(false);
      }
    };

    const handleDeny = async (requestId: string) => {
      setIsProcessing(true);
      try {
        await uiDuplicationRequestStore.denyDuplicationRequest(
          requestId,
          responseMessage.trim() || undefined,
        );
        setResponseMessage('');
        setSelectedRequestId(null);
        // Real-time listener will automatically update the requests
      } catch (error) {
        console.error('Failed to deny request:', error);
      } finally {
        setIsProcessing(false);
      }
    };

    if (!open) return null;

    return (
      <Box
        onClick={onClose}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1300,
        }}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            bgcolor: designTokens.colors.bg.elevated,
            border: `1px solid ${designTokens.colors.border.hover}`,
            borderRadius: designTokens.spacing.radiusLg,
            padding: '24px',
            maxWidth: 600,
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              fontSize: designTokens.typography.fontSize.lg,
              fontWeight: designTokens.typography.fontWeight.bold,
              color: designTokens.colors.text.primary,
              mb: 3,
            }}
          >
            Duplication Requests
          </Box>

          {/* Loading state */}
          {uiDuplicationRequestStore.isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: designTokens.colors.accent.primary }} />
            </Box>
          )}

          {/* Error state */}
          {uiDuplicationRequestStore.error && (
            <Box
              sx={{
                bgcolor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: designTokens.spacing.radiusSm,
                padding: '12px 16px',
                mb: 3,
                fontSize: designTokens.typography.fontSize.sm,
                color: '#ef4444',
              }}
            >
              {uiDuplicationRequestStore.error}
            </Box>
          )}

          {/* Requests list */}
          {!uiDuplicationRequestStore.isLoading && (
            <>
              {pendingRequests.length === 0 ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    padding: '32px',
                    color: designTokens.colors.text.dim,
                    fontSize: designTokens.typography.fontSize.sm,
                  }}
                >
                  No pending duplication requests
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {pendingRequests.map(({ id: requestId, request }) => {
                    const isSelected = selectedRequestId === requestId;

                    return (
                      <Box
                        key={requestId}
                        sx={{
                          border: `1px solid ${
                            isSelected
                              ? designTokens.colors.border.hover
                              : designTokens.colors.border.default
                          }`,
                          borderRadius: designTokens.spacing.radiusMd,
                          padding: '16px',
                          bgcolor: isSelected
                            ? designTokens.colors.bg.cardHover
                            : designTokens.colors.bg.card,
                          cursor: 'pointer',
                          transition: `all ${designTokens.transitions.fast}`,
                          '&:hover': {
                            borderColor: designTokens.colors.border.hover,
                            bgcolor: designTokens.colors.bg.cardHover,
                          },
                        }}
                        onClick={() => setSelectedRequestId(requestId || null)}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 1,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ color: designTokens.colors.text.dim }}>
                              <PersonIcon />
                            </Box>
                            <Box
                              sx={{
                                fontSize: designTokens.typography.fontSize.sm,
                                fontWeight: designTokens.typography.fontWeight.bold,
                                color: designTokens.colors.text.primary,
                              }}
                            >
                              {request.name}
                            </Box>
                            <Box
                              sx={{
                                fontSize: designTokens.typography.fontSize.xs,
                                color: designTokens.colors.text.dim,
                              }}
                            >
                              ({request.email})
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              fontSize: designTokens.typography.fontSize.xs,
                              color: designTokens.colors.text.dim,
                              fontFamily: designTokens.typography.fontMono,
                            }}
                          >
                            {new Date(request.requestedAt).toLocaleDateString()}
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            fontSize: designTokens.typography.fontSize.sm,
                            color: designTokens.colors.text.secondary,
                            mb: 1,
                          }}
                        >
                          Wants to duplicate: <strong>{request.sessionName}</strong>
                        </Box>
                        <Box
                          sx={{
                            fontSize: designTokens.typography.fontSize.sm,
                            color: designTokens.colors.text.secondary,
                            mb: 1,
                          }}
                        >
                          New name: <strong>{request.newSessionName}</strong>
                        </Box>
                        {request.message && (
                          <Box
                            sx={{
                              fontSize: designTokens.typography.fontSize.xs,
                              color: designTokens.colors.text.dim,
                              fontStyle: 'italic',
                              mt: 1,
                              padding: '8px',
                              bgcolor: designTokens.colors.bg.elevated,
                              borderRadius: designTokens.spacing.radiusSm,
                            }}
                          >
                            "{request.message}"
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Response section */}
              {selectedRequest && (
                <Box
                  sx={{
                    mt: 3,
                    padding: '16px',
                    bgcolor: designTokens.colors.bg.elevated,
                    borderRadius: designTokens.spacing.radiusMd,
                    border: `1px solid ${designTokens.colors.border.default}`,
                  }}
                >
                  <Box
                    sx={{
                      fontSize: designTokens.typography.fontSize.sm,
                      fontWeight: designTokens.typography.fontWeight.bold,
                      color: designTokens.colors.text.primary,
                      mb: 2,
                    }}
                  >
                    Respond to {selectedRequest.name}
                  </Box>
                  <Box
                    component="textarea"
                    value={responseMessage}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setResponseMessage(e.target.value)
                    }
                    disabled={isProcessing}
                    placeholder="Add a message to your response (optional)..."
                    rows={3}
                    sx={{
                      width: '100%',
                      padding: '12px 14px',
                      bgcolor: designTokens.colors.bg.card,
                      border: `1px solid ${designTokens.colors.border.default}`,
                      borderRadius: designTokens.spacing.radiusSm,
                      fontFamily: designTokens.typography.fontMono,
                      fontSize: designTokens.typography.fontSize.sm,
                      color: designTokens.colors.text.primary,
                      outline: 'none',
                      resize: 'vertical',
                      transition: `all ${designTokens.transitions.fast}`,
                      mb: 2,
                      '&:focus': {
                        borderColor: designTokens.colors.border.hover,
                      },
                      '&::placeholder': {
                        color: designTokens.colors.text.dim,
                      },
                      '&:disabled': {
                        opacity: 0.5,
                        cursor: 'not-allowed',
                      },
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Box
                      component="button"
                      onClick={() => {
                        if (selectedRequestId) {
                          handleApprove(selectedRequestId);
                        }
                      }}
                      disabled={isProcessing || !selectedRequestId}
                      aria-label="Approve duplication request"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        padding: '10px 20px',
                        bgcolor: designTokens.colors.status.active,
                        border: 'none',
                        borderRadius: designTokens.spacing.radiusSm,
                        fontFamily: designTokens.typography.fontMono,
                        fontSize: designTokens.typography.fontSize.sm,
                        fontWeight: designTokens.typography.fontWeight.bold,
                        color: '#fff',
                        cursor: isProcessing ? 'not-allowed' : 'pointer',
                        transition: `all ${designTokens.transitions.fast}`,
                        opacity: isProcessing ? 0.5 : 1,
                        '&:hover': {
                          bgcolor: isProcessing ? undefined : designTokens.colors.status.active,
                          filter: isProcessing ? undefined : 'brightness(0.9)',
                        },
                      }}
                    >
                      {isProcessing ? (
                        <CircularProgress size={18} sx={{ color: '#fff' }} />
                      ) : (
                        <>
                          <CheckIcon />
                          Approve
                        </>
                      )}
                    </Box>
                    <Box
                      component="button"
                      onClick={() => {
                        if (selectedRequestId) {
                          handleDeny(selectedRequestId);
                        }
                      }}
                      disabled={isProcessing || !selectedRequestId}
                      aria-label="Deny duplication request"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        padding: '10px 20px',
                        bgcolor: designTokens.colors.status.paused,
                        border: 'none',
                        borderRadius: designTokens.spacing.radiusSm,
                        fontFamily: designTokens.typography.fontMono,
                        fontSize: designTokens.typography.fontSize.sm,
                        fontWeight: designTokens.typography.fontWeight.bold,
                        color: '#fff',
                        cursor: isProcessing ? 'not-allowed' : 'pointer',
                        transition: `all ${designTokens.transitions.fast}`,
                        opacity: isProcessing ? 0.5 : 1,
                        '&:hover': {
                          bgcolor: isProcessing ? undefined : designTokens.colors.status.paused,
                          filter: isProcessing ? undefined : 'brightness(0.85)',
                        },
                      }}
                    >
                      {isProcessing ? (
                        <CircularProgress size={18} sx={{ color: '#fff' }} />
                      ) : (
                        <>
                          <CloseIcon />
                          Deny
                        </>
                      )}
                    </Box>
                    <Box
                      component="button"
                      onClick={() => {
                        setSelectedRequestId(null);
                        setResponseMessage('');
                      }}
                      disabled={isProcessing}
                      aria-label="Cancel response"
                      sx={{
                        padding: '10px 16px',
                        bgcolor: 'transparent',
                        border: `1px solid ${designTokens.colors.border.default}`,
                        borderRadius: designTokens.spacing.radiusSm,
                        fontFamily: designTokens.typography.fontMono,
                        fontSize: designTokens.typography.fontSize.sm,
                        color: designTokens.colors.text.secondary,
                        cursor: isProcessing ? 'not-allowed' : 'pointer',
                        transition: `all ${designTokens.transitions.fast}`,
                        opacity: isProcessing ? 0.5 : 1,
                        '&:hover': {
                          borderColor: designTokens.colors.border.hover,
                          color: designTokens.colors.text.primary,
                        },
                      }}
                    >
                      Cancel
                    </Box>
                  </Box>
                </Box>
              )}
            </>
          )}

          {/* Close button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Box
              component="button"
              onClick={onClose}
              disabled={isProcessing}
              sx={{
                padding: '10px 16px',
                bgcolor: 'transparent',
                border: `1px solid ${designTokens.colors.border.default}`,
                borderRadius: designTokens.spacing.radiusSm,
                fontFamily: designTokens.typography.fontMono,
                fontSize: designTokens.typography.fontSize.sm,
                color: designTokens.colors.text.secondary,
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                transition: `all ${designTokens.transitions.fast}`,
                opacity: isProcessing ? 0.5 : 1,
                '&:hover': {
                  borderColor: designTokens.colors.border.hover,
                  color: designTokens.colors.text.primary,
                },
              }}
            >
              Close
            </Box>
          </Box>
        </Box>
      </Box>
    );
  },
);
